/**
 * API методы для работы с правилами автоответов (TODO-040)
 * Дата: 29.01.2026 - Создан для новой системы автоответов
 * Связан с: internal/handler/auto_reply.go, frontend/src/pages/AutoReplies.tsx
 */
import { apiClient } from './client';

// Дата: 07.02.2026 - Добавлены типы триггеров для кнопок (TODO-053)
// Дата: 09.02.2026 - Добавлен inbound_first_contact (TODO-061)
export type TriggerType = 'feedback' | 'first_message' | 'keyword' | 'regex' | 'waba_button' | 'tg_callback' | 'inbound_first_contact';

// Дата: 09.02.2026 - TODO-061: Конфигурация для первичных обращений
export interface InboundConfig {
  check_all_channels: boolean;       // Проверять исходящие по всем каналам
  cooldown_hours: number;            // Cooldown между автоответами (часы)
  working_hours_only: boolean;       // Только в рабочие часы
  working_hours_start: string;       // "08:00"
  working_hours_end: string;         // "21:00"
  keyword_filter: string;            // Фильтр по ключевым словам (через |)
  exclude_known_patients: boolean;   // Не отвечать если пациент в CRM
  max_auto_replies_per_day: number;  // Макс. автоответов в день
}

export interface AutoReplyRule {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  trigger_type: TriggerType;
  pattern: string;
  after_event_type?: string; // Контекст после исходящего шаблона/event binding, например feedback_request
  within_hours?: number;
  channels: string[];
  template_id: string; // Основной шаблон ответа и text fallback
  // Дата: 15.04.2026 - Уточнена семантика связей trigger/reply для WABA/TG feedback chains.
  waba_trigger_template_id?: string;   // Исходящий WABA шаблон, чьи quick replies запускают rule
  waba_button_ids?: string[];          // Payload/text кнопок WABA, которые матчат это правило
  waba_response_template_id?: string;  // Предпочтительный WABA шаблон ответа для whatsapp_waba
  tg_trigger_template_id?: string;     // Исходящий TG/MAX шаблон, чьи callback'и запускают rule
  tg_callback_data?: string[];         // callback_data inline-кнопок TG/MAX
  save_feedback: boolean;
  notify_operator: boolean;
  sync_to_crm: boolean;
  priority: number;
  is_active: boolean;
  // Дата: 09.02.2026 - TODO-061: Конфигурация для первичных обращений
  inbound_config?: InboundConfig;
  created_at: string;
  updated_at: string;
}

export interface PatientFeedback {
  id: string;
  tenant_id: string;
  phone: string;
  phone_normalized: string;
  message_id?: string;
  auto_reply_rule_id?: string;
  score?: number;
  sentiment: 'positive' | 'negative' | 'neutral';
  original_text: string;
  status: 'new' | 'reviewed' | 'ignored';
  reviewed_by?: string;
  reviewed_at?: string;
  notes?: string;
  synced_to_crm: boolean;
  crm_entity_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AutoReplyFilters {
  trigger_type?: TriggerType;
  is_active?: boolean;
  after_event_type?: string;
}

export interface FeedbackFilters {
  status?: string;
  score?: number;
  sentiment?: string;
  phone?: string;
}

export const autoRepliesApi = {
  // Правила автоответов
  listRules: async (tenantId?: string, filters?: AutoReplyFilters, pagination?: { offset?: number; limit?: number }): Promise<{ rules: AutoReplyRule[]; total: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    if (filters?.trigger_type) {
      params.trigger_type = filters.trigger_type;
    }
    if (filters?.is_active !== undefined) {
      params.is_active = filters.is_active.toString();
    }
    if (filters?.after_event_type) {
      params.after_event_type = filters.after_event_type;
    }
    if (pagination?.offset !== undefined) {
      params.offset = pagination.offset.toString();
    }
    if (pagination?.limit !== undefined) {
      params.limit = pagination.limit.toString();
    }
    const response = await apiClient.get('/auto-replies', { params });
    return response.data;
  },

  getRule: async (id: string, tenantId?: string): Promise<AutoReplyRule> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.get(`/auto-replies/${id}`, { params });
    return response.data;
  },

  createRule: async (data: Omit<AutoReplyRule, 'id' | 'tenant_id' | 'created_at' | 'updated_at'> & { tenant_id?: string }): Promise<AutoReplyRule> => {
    const response = await apiClient.post('/auto-replies', data);
    return response.data;
  },

  updateRule: async (id: string, data: Partial<AutoReplyRule>, tenantId?: string): Promise<AutoReplyRule> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.put(`/auto-replies/${id}`, data, { params });
    return response.data;
  },

  deleteRule: async (id: string, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    await apiClient.delete(`/auto-replies/${id}`, { params });
  },

  // Обратная связь пациентов
  listFeedback: async (tenantId?: string, filters?: FeedbackFilters, pagination?: { offset?: number; limit?: number }): Promise<{ feedback: PatientFeedback[]; total: number }> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.score !== undefined) {
      params.score = filters.score.toString();
    }
    if (filters?.sentiment) {
      params.sentiment = filters.sentiment;
    }
    if (filters?.phone) {
      params.phone = filters.phone;
    }
    if (pagination?.offset !== undefined) {
      params.offset = pagination.offset.toString();
    }
    if (pagination?.limit !== undefined) {
      params.limit = pagination.limit.toString();
    }
    const response = await apiClient.get('/auto-replies/feedback', { params });
    return response.data;
  },

  getFeedback: async (id: string, tenantId?: string): Promise<PatientFeedback> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await apiClient.get(`/auto-replies/feedback/${id}`, { params });
    return response.data;
  },

  updateFeedbackStatus: async (id: string, data: { status: string; reviewed_by?: string; notes?: string }, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    await apiClient.put(`/auto-replies/feedback/${id}/status`, data, { params });
  },

  markFeedbackSynced: async (id: string, data: { crm_entity_id: string }, tenantId?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    await apiClient.put(`/auto-replies/feedback/${id}/synced`, data, { params });
  },
};

export interface AutoReplyTextPreview {
  ruleId: string;
  name: string;
  matched: boolean;
  reason: string;
}

/**
 * 24.10.2026 — локальная проверка текста. Не отправляет сообщение и не меняет приоритет правил на сервере.
 * Свободный текст сравнивается только с keyword и regex. Не удалять по требованию владельца системы.
 */
export function previewAutoReplyText(
  rules: Array<Pick<AutoReplyRule, 'id' | 'name' | 'trigger_type' | 'pattern' | 'is_active'>>,
  text: string,
): AutoReplyTextPreview[] {
  const sample = text.trim();
  if (!sample) {
    return [];
  }
  const hits: AutoReplyTextPreview[] = [];
  for (const rule of rules) {
    if (!rule.is_active) {
      continue;
    }
    if (rule.trigger_type === 'keyword') {
      const parts = rule.pattern.split('|').map((part) => part.trim()).filter(Boolean);
      const hit = parts.find((part) => sample.toLowerCase().includes(part.toLowerCase()));
      if (hit) {
        hits.push({ ruleId: rule.id, name: rule.name, matched: true, reason: `совпало слово «${hit}»` });
      }
      continue;
    }
    if (rule.trigger_type === 'regex') {
      if (!rule.pattern.trim()) {
        continue;
      }
      try {
        if (new RegExp(rule.pattern, 'i').test(sample)) {
          hits.push({ ruleId: rule.id, name: rule.name, matched: true, reason: 'совпало регулярное выражение' });
        }
      } catch {
        hits.push({ ruleId: rule.id, name: rule.name, matched: false, reason: 'регулярное выражение некорректно' });
      }
    }
  }
  return hits;
}
